import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession } from '../../utils/authUtils';
import { markOnboardingComplete } from '../../utils/onboardingUtils';
import logo from '../../assets/VisibeenLogo.png';
import Footer from '../Layouts/Footer';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import EmailIcon from '../icons/EmailIcon';
import {
  CheckCircle,
  Circle,
  Play,
  AlertCircle,
  Clock,
  ChevronRight,
  Sparkles,
  LayoutDashboard,
  BarChart3,
  Search,
  Rocket,
  Star,
  Zap
} from 'lucide-react';
import './ClientOnboardingGuide.css';

// Steps Data
const onboardingSteps = [
  {
    id: 1,
    title: 'Login & Access',
    description: 'Sign in using your registered mobile or email to access Dashboard, Business Profile & Reports.',
    icon: LayoutDashboard,
  },
  {
    id: 2,
    title: 'Dashboard Overview',
    description: 'Check your Visibility Score and review current online performance metrics.',
    icon: BarChart3,
  },
  {
    id: 3,
    title: 'Competitor Analysis',
    description: 'Select your business category & location. Run benchmarking score (Out of 1000).',
    icon: BarChart3,
  },
  {
    id: 4,
    title: 'Add Keywords',
    description: 'Add service-based keywords customers search on Google for better visibility.',
    icon: Search,
  },
  {
    id: 5,
    title: 'Activate Monthly Plan',
    description: 'Choose your monthly plan and activate AI services to unlock full potential.',
    icon: Rocket,
  },
  {
    id: 6,
    title: 'Reputation Management',
    description: 'Enable review monitoring. Respond to Google reviews using AI suggestions.',
    icon: Star,
  },
  {
    id: 7,
    title: 'AI Optimization',
    description: 'Activate AI optimization and let Visibeen improve your rankings automatically.',
    icon: Zap,
  },
];

// Help Videos Data
const helpVideos = [
  { id: 1, title: 'Login & Dashboard Guide', duration: '2:30', category: 'Getting Started', videoId: 'vaHGwKmkSic' },
  { id: 2, title: 'Running Competitor Analysis', duration: '3:45', category: 'Dashboard', videoId: '6dmSdiup2pE' },
  { id: 3, title: 'Monthly Plan', duration: '4:15', category: 'Analysis', videoId: 'pkjlvOn2nh4' },
  { id: 4, title: 'Check Your Performance', duration: '3:00', category: 'SEO', videoId: 'wJLacAAFSsc' },
  { id: 5, title: 'Free Website', duration: '5:00', category: 'Website', videoId: '26nYMq7GUvc' },
  { id: 6, title: 'EDM Social Media Post', duration: '5:00', category: 'Social Media', videoId: 'v3SBwuBqAfc' },
];

// Support Matrix Data
const supportMatrix = [
  {
    level: 1,
    title: 'Customer Support',
    contact: '+91 9115959722',
    contactLink: 'https://wa.me/919115959722?text=Hi%20Visibeen%20Support%2C%20I%20need%20help%20with%20my%20account.',
    responseTime: 'Within 2 hours',
    icon: 'whatsapp',
  },
  {
    level: 2,
    title: 'Technical Support',
    contact: 'support@visibeen.com',
    contactLink: 'mailto:support@visibeen.com?subject=Technical%20Support%20Request',
    responseTime: 'Within 24 hours',
    icon: 'email',
  },
  {
    level: 3,
    title: 'Management Escalation',
    contacts: [
      { label: 'kirti@e2edigitech.com', link: 'mailto:kirti@e2edigitech.com?subject=Urgent%20-%20Management%20Escalation' },
      { label: 'ruchi@e2edigitech.com', link: 'mailto:ruchi@e2edigitech.com?subject=Urgent%20-%20Management%20Escalation' }
    ],
    responseTime: 'Within 48 hours',
    icon: AlertCircle,
    urgent: true,
  },
];

const ClientOnboardingGuide = () => {
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showVideoModal, setShowVideoModal] = useState(null);
  const session = getSession();

  useEffect(() => {
    const saved = localStorage.getItem('visibeen_onboarding_steps');
    if (saved) {
      setCompletedSteps(JSON.parse(saved));
    }
  }, []);

  const handleStepToggle = (stepId) => {
    const newCompleted = completedSteps.includes(stepId)
      ? completedSteps.filter(id => id !== stepId)
      : [...completedSteps, stepId];
    setCompletedSteps(newCompleted);
    localStorage.setItem('visibeen_onboarding_steps', JSON.stringify(newCompleted));
  };

  const handleGoToDashboard = () => {
    if (session) {
      markOnboardingComplete(session);
    }
    navigate('/dashboard', { replace: true });
  };

  return (
    <div
      className="onboarding-page"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        overflow: 'auto',
        backgroundColor: '#f8fafc'
      }}
    >
      {/* Header */}
      <header className="onboarding-header">
        <div className="header-container">
          <img src={logo} alt="Visibeen" className="header-logo-img" />
          <button className="goto-dashboard-btn" onClick={handleGoToDashboard}>
            <span>Go to Dashboard</span>
            <ChevronRight size={16} />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="onboarding-main">
        <div className="container">
          {/* Hero Section */}
          <section className="hero-section">
            <div className="hero-content">
              {/* Celebration particles */}
              <div className="celebration-container">
                <div className="confetti confetti-1"></div>
                <div className="confetti confetti-2"></div>
                <div className="confetti confetti-3"></div>
                <div className="confetti confetti-4"></div>
                <div className="confetti confetti-5"></div>
                <div className="confetti confetti-6"></div>
                <div className="confetti confetti-7"></div>
                <div className="confetti confetti-8"></div>
                <div className="sparkle sparkle-1">✨</div>
                <div className="sparkle sparkle-2">🎉</div>
                <div className="sparkle sparkle-3">✨</div>
                <div className="sparkle sparkle-4">🎊</div>
              </div>
              <h1 className="hero-title">Welcome to Visibeen</h1>
              <p className="hero-subtitle">
                Complete these simple steps to set up your account and start improving your online visibility
              </p>
            </div>
          </section>

          {/* Steps Section */}
          <section className="steps-section">
            <h2 className="section-title">Setup Checklist</h2>
            <p className="section-subtitle">Click on each step to mark as complete</p>

            <div className="steps-grid">
              {onboardingSteps.map((step) => {
                const isCompleted = completedSteps.includes(step.id);
                const IconComponent = step.icon;

                return (
                  <div
                    key={step.id}
                    className={`step-card ${isCompleted ? 'completed' : ''}`}
                    onClick={() => handleStepToggle(step.id)}
                  >
                    <div className="step-header">
                      <div className="step-icon">
                        <IconComponent size={20} />
                      </div>
                      <div className="step-number">Step {step.id}</div>
                      <div className="step-check">
                        {isCompleted ? <CheckCircle size={20} /> : <Circle size={20} />}
                      </div>
                    </div>
                    <h3 className="step-title">{step.title}</h3>
                    <p className="step-description">{step.description}</p>
                  </div>
                );
              })}
            </div>

            {/* CTA Button */}
            <div className="cta-container">
              <button className="cta-button" onClick={handleGoToDashboard}>
                <span>Go to Dashboard</span>
                <ChevronRight size={18} />
              </button>
            </div>
          </section>

          {/* Videos Section */}
          <section className="videos-section">
            <h2 className="section-title">Quick Help Videos</h2>
            <p className="section-subtitle">Learn features in minutes with our video guides</p>

            <div className="videos-grid">
              {helpVideos.map((video) => (
                <div
                  key={video.id}
                  className="video-card"
                  onClick={() => setShowVideoModal(video)}
                >
                  <div className="video-thumbnail" style={{ backgroundImage: `url(https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg)` }}>
                    <div className="play-button">
                      <Play size={24} />
                    </div>
                    <span className="video-duration">{video.duration}</span>
                  </div>
                  <div className="video-info">
                    <span className="video-category">{video.category}</span>
                    <h4 className="video-title">{video.title}</h4>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Support Section */}
          <section className="support-section">
            <h2 className="section-title">Need Help?</h2>
            <p className="section-subtitle">Our support team is here to assist you</p>

            <div className="support-grid">
              {supportMatrix.map((support) => {
                // Render the appropriate icon based on type
                const renderIcon = () => {
                  if (support.icon === 'whatsapp') {
                    return <WhatsAppIcon width={18} height={18} className="info-icon" />;
                  } else if (support.icon === 'email') {
                    return <EmailIcon width={18} height={18} className="info-icon" />;
                  } else {
                    const IconComponent = support.icon;
                    return <IconComponent size={18} className="info-icon" />;
                  }
                };

                return (
                  <div
                    key={support.level}
                    className={`support-card level-${support.level}`}
                  >
                    <div className="support-header">
                      <span className="support-level">LEVEL {support.level}</span>
                      <h4 className="support-title">{support.title}</h4>
                    </div>
                    <div className="support-separator" />
                    <div className="support-info">
                      <div className="info-row">
                        {renderIcon()}
                        <div className="info-content">
                          <span className="info-label">Contact</span>
                          {support.contacts ? (
                            <div className="contact-column" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {support.contacts.map((contact, idx) => (
                                <a
                                  key={idx}
                                  href={contact.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="info-value contact-link"
                                >
                                  {contact.label}
                                </a>
                              ))}
                            </div>
                          ) : (
                            <a
                              href={support.contactLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="info-value contact-link"
                            >
                              {support.contact}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="info-row">
                        <Clock size={18} className="info-icon" />
                        <div className="info-content">
                          <span className="info-label">Response Time</span>
                          <span className="info-value">
                            {support.responseTime}
                            {support.urgent && <span className="urgent-badge">Urgent</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <div className="onboarding-footer-wrapper">
        <Footer />
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="video-modal-overlay" onClick={() => setShowVideoModal(null)}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showVideoModal.title}</h3>
              <button className="modal-close" onClick={() => setShowVideoModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="video-embed">
                <iframe
                  src={`https://www.youtube.com/embed/${showVideoModal.videoId}?autoplay=1&rel=0`}
                  title={showVideoModal.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOnboardingGuide;
